/**
 * Q&Aダイアログ用のカスタムフック
 * Q&A画面の状態管理とAPI呼び出しを処理
 */
"use client";
import React, { useState } from "react";
import { hooks, apijs } from "@lib/client";
import Consts from  "../../../../config/consts.js";
import logjs from "@metrojs/logjs";

import QaDialog from "./QaDialog.js";

const api = new apijs("api/QaDialog");
const log = new logjs("useQaDialog");

/**
 * Q&Aダイアログ用のカスタムフック
 * Q&A表示・未回答一覧・回答の確認/送信に必要な状態とAPI呼び出しを提供する。
 * @param {Object} initState フォームの初期状態
 * @param {Object} state 追加の状態
 * @param {Object} actions アクション関数（showLoading/hideLoading, showAlert 等）
 * @returns {[Object, Object]} [formData, formProps] 画面表示用データと操作関数群
 */
export function useQaDialog(initState = {}, state = {}, actions = {}) {
  const { open, onClose, isOpen, dialogProps, renderDialog } = hooks.useCustomDialog(QaDialog, { resetOnClose: true });

  const [form, formProps] = hooks.useFormEx(initState, state, actions);

  // 状態管理
  const [qaData, setQaData] = useState(null);
  const [partsData, setPartsData] = useState(null);
  const [mikaitoList, setMikaitoList] = useState([]);
  const [qaList, setQaList] = useState([]);
  const [qaCnt, setQaCnt] = useState(0);

  const [isShowMikaitoList, setIsShowMikaitoList] = useState(true);
  const [isShowInit, setIsShowInit] = useState(true);
  const [isAnswerOpen, setIsAnswerOpen] = useState(false);
  const [isShowConfirm, setIsShowConfirm] = useState(false);
  const [isShowFinish, setIsShowFinish] = useState(false);

  const [selectedQa, setSelectedQa] = useState(null);
  const [loading, setLoading] = useState(false);

  /**
   * Q&Aデータの取得
   * @param {string} mitsumoriNo 見積番号
   * @param {string|null} [buhinId] 部品ID（任意）
   * @returns {Promise<Object>} { result, data, mikaitoList, qaList, qaCnt, isAnswerOpen, error? }
   */
  async function init(mitsumoriNo, buhinId = null) {
    log.debug("START init", { mitsumoriNo, buhinId });
    setLoading(true);
    try {
      actions.showLoading();

      const result = await api.post({
        mode: "init",
        MitsumoriNo: mitsumoriNo,
        buhinId: buhinId,
      });

      log.debug("init result", result);

      if (result.result) {
        setQaData(result.qaData);
        setPartsData(result.data);
        //const _qaList = normalizedQaList(result.qaList || []);
        setQaList(result.qaList || []);
        setQaCnt(result.qaCnt || 0);
        //const _mikaitoList = normalizedQaList(result.mikaitoList || []);
        setMikaitoList(result.mikaitoList || []);
        setIsAnswerOpen(result.isAnswerOpen || false);
      }

      return result;
    } catch (error) {
      log.error("init error", error);
      return { error: error.message, result: false };
    } finally {
      setLoading(false);
      actions.hideLoading();
    }
  }

  /**
   * 質問の選択処理
   * @param {Object} qa 選択されたQ&Aデータ
   * @returns {void}
   */
  function selectQa(qa) {
    log.debug("START selectQa", qa);
    setSelectedQa(qa);
  }

  /**
   * 回答の送信処理
   * @returns {Promise<Object>} { result, action, error? }
   */
  async function submitAnswer() {
    log.debug("START submitAnswer",form);
    log.debug("START selectedQa",selectedQa);
    try {
      actions.showLoading();
      const answerData = {
        MitsumoriNo: qaData.MitsumoriNo,
        PRID: selectedQa?.PRID,
        QAnaiyo: form.QAnaiyo,
        Q_no: selectedQa?.Q_no || 1,
        CorpID: selectedQa?.CorpID || 1,
      };
      log.debug("submitAnswer answerData", answerData);
      const result = await api.post({
        mode: "finish",
        ...answerData,
      });

      log.debug("submitAnswer result", result);

      if (result.result) {
        // 成功時の処理
        if (result.action === "qa_finish") {
          // 回答完了後の処理
          await init(answerData.MitsumoriNo, answerData.PRID);
        }
      }

      return result;
    } catch (error) {
      log.error("submitAnswer error", error);
      return { error: error.message, result: false };
    } finally {
      actions.hideLoading();
    }
  }

  /**
   * 質問の送信処理
   * @returns {Promise<Object>} { result, action, error? }
   */
  async function submitQuestion() {
    log.debug("START submitQuestion",form);
    log.debug("START qaData",qaData);
    try {
      actions.showLoading();
      const questionData = {
        MitsumoriNo: qaData.MitsumoriNo,
        PRID: qaData.PRID,
        QAnaiyo: form.QAnaiyo,
        //Q_no: selectedQa?.Q_no || 1,
        //CorpID: selectedQa?.CorpID || 1,
      };
      log.debug("submitQuestion questionData", questionData);
      const result = await api.post({
        mode: "finishQ",
        ...questionData,
      });

      log.debug("submitQuestion result", result);

      if (result.result) {
        // 成功時の処理
        if (result.action === "qa_finish") {
          // 回答完了後の処理
          await init(questionData.MitsumoriNo, questionData.PRID);
        }
      }

      return result;
    } catch (error) {
      log.error("submitQuestion error", error);
      return { error: error.message, result: false };
    } finally {
      actions.hideLoading();
    }
  }

  /**
   * 回答の確認処理
   * @returns {Promise<Object>} { result, action, error? }
   */
  async function confirm() {
    log.debug("START confirm",form);
    log.debug("START qaData",qaData);

    try {
      actions.showLoading();
      const answerData = {
        MitsumoriNo: qaData.MitsumoriNo,
        QAnaiyo: form.QAnaiyo,
      };
      const result = await api.post({
        mode: "confirm",
        ...answerData,
      });

      log.debug("confirm result", result);

      if (result.result) {
        setIsShowInit(false);
        setIsShowConfirm(true);
        setIsAnswerOpen(false);
        setIsShowFinish(false);
      }

      return result;
    } catch (error) {
      log.error("confirmAnswer error", error);
      return { error: error.message, result: false };
    } finally {
      actions.hideLoading();
    }
  }

  // Q→A の順となるよう、qaList を安定整形（ツリー/フラット双方に対応）
  function normalizedQaList(qaList) {
    log.debug("START normalizedQaList", qaList);
    if (!Array.isArray(qaList) || qaList.length === 0) return [];

    // すでに Q に A 配列がぶら下がるツリー構造の場合 → 並び替えのみ
    const isTree = qaList.every((q) => typeof q === "object" && "Q_no" in q && Array.isArray(q.A));
    if (isTree) {
      const clone = qaList.map((q) => ({
        ...q,
        A: Array.isArray(q.A) ? [...q.A] : [],
      }));
      // Q_no 昇順
      clone.sort((a, b) => (a.Q_no ?? 0) - (b.Q_no ?? 0));
      // 各Q配下のAを Seqnum → created で安定ソート
      clone.forEach((q) => {
        q.A.sort((a, b) => {
          const as = a.Seqnum ?? 0;
          const bs = b.Seqnum ?? 0;
          if (as !== bs) return as - bs;
          const at = a.created ? new Date(a.created).getTime() : 0;
          const bt = b.created ? new Date(b.created).getTime() : 0;
          return at - bt;
        });
      });
      return clone;
    }

    // フラット配列（QとAが混在）を Q→A のツリーに変換しつつ安定ソート
    const sorted = [...qaList].sort((a, b) => {
      if ((a.Q_no ?? 0) !== (b.Q_no ?? 0)) return (a.Q_no ?? 0) - (b.Q_no ?? 0);
      const as = a.Seqnum ?? 0;
      const bs = b.Seqnum ?? 0;
      if (as !== bs) return as - bs;
      if ((a.QA_flg ?? 0) !== (b.QA_flg ?? 0)) return (a.QA_flg ?? 0) - (b.QA_flg ?? 0); // Q(1)が先
      const at = a.created ? new Date(a.created).getTime() : 0;
      const bt = b.created ? new Date(b.created).getTime() : 0;
      return at - bt;
    });

    const map = new Map();
    for (const row of sorted) {
      const qno = row.Q_no ?? 0;
      if (row.QA_flg === Consts.QA_TYPE.TYPE_Q) {
        map.set(qno, { ...row, A: [] });
      } else {
        const q = map.get(qno);
        if (q) {
          q.A.push({ ...row });
        } else {
          // Qが先に来ていない場合のプレースホルダ
          map.set(qno, {
            ...row,
            QA_flg: Consts.QA_TYPE.TYPE_Q,
            A: [{ ...row }],
          });
        }
      }
    }

    const arr = Array.from(map.values());
    // Q_no 昇順で返す（AはすでにSeqnum/created順）
    arr.sort((a, b) => (a.Q_no ?? 0) - (b.Q_no ?? 0));
    return arr;
  }
  // フォームデータとプロパティを返す
  return [
    {
      form,
      qaData,
      partsData,
      mikaitoList,
      qaList,
      qaCnt,
      isAnswerOpen,
      isShowConfirm,
      isShowFinish,
      isShowMikaitoList,
      isShowInit,
      selectedQa,
      loading,
    },
    {
      ...formProps,
      init,
      selectQa,
      submitAnswer,
      submitQuestion,
      confirm,
      open,
      onClose,
      setIsShowConfirm,
      setIsShowFinish,
      setIsAnswerOpen,
      setIsShowInit,
      setIsShowMikaitoList,
      renderDialog,
    },
  ];
}

export default useQaDialog;
